import { Box, Popover, Typography, IconButton, Skeleton } from "@mui/material";
import ResponsiveCoverages from "./ResponsiveCoverages/ResponsiveCoverages";
import { CoveragesStyles } from "./CoveragesStyles";
import Premium from "./Premium/Premium";
import CoveragesAndLimits from "./CoveragesAndLimits/CoveragesAndLimits";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../store/store";
import { IColumn } from "../../../../common/CustomTable/CustomTable";
import { currencyFormat } from "../../../../utils/Utility";
import Discounts from "./Discounts/Discounts";
import { GetPolicyCoveragesAndDiscounts } from "../../../../store/user/policyDetails/policyDetailsActions";
import { includesCoverages } from "../../../../constants/Constants";
import CoveragesAndLimitsStyles from "./CoveragesAndLimits/CoveragesAndLimitsStyles";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import AdditionalDeductibles from "./AdditionalDeductibles/AdditionalDeductibles";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../common/CustomSkeleton/CustomSkeleton";

const Coverages = () => {
  const dispatch = useAppDispatch();
  const { coveragesAndDiscounts } = useAppSelector(
    (store: RootState) => store.policyDetails
  );
  const { policyId, carrier, system, lobCode } = useParams();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverContent, setPopoverContent] = useState<string>("");

  const { t } = useTranslation();
  const { calledReducerType, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );

  useEffect(() => {
    if (policyId && carrier && lobCode && system) {
      dispatch(
        GetPolicyCoveragesAndDiscounts({
          policyId,
          carrier: carrier,
          system: system,
          lobCode: lobCode,
        })
      );
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    content: string
  ) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(content);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverContent("");
  };

  const open = Boolean(anchorEl);

  const getCoverageData = () => {
    const dataArray: any[] = [];
    const additionalCoverageArray: any[] = [];
    const headerArray: IColumn[] = [
      {
        label: t("Coverages"),
        Cell: (row: any) => {
          return (
            <>
              <Box 
                sx={{ display: "flex", alignItems: "center" }}
                id="coverages_name_container"
                data-test-id="coverages_name_container"
                aria-label="Coverage name container"
              >
                <Typography 
                  sx={CoveragesStyles.coverageText}
                  id="coverages_name"
                  data-test-id="coverages_name"
                  aria-label={`Coverage name: ${row ? row.coverageName : ""}`}
                >
                  {row ? row.coverageName : ""}
                </Typography>
                {/* {row && row.description ? (
                  <IconButton
                    onMouseEnter={(e) => handlePopoverOpen(e, row.description)}
                    onMouseLeave={handlePopoverClose}
                  >
                    <InfoOutlinedIcon />
                  </IconButton>
                ) : (
                  <></>
                )} */}
              </Box>
              <Box
                id="coverages_subtext_container"
                data-test-id="coverages_subtext_container"
                aria-label="Coverage subtext container"
              >
                <Typography 
                  sx={CoveragesStyles.coverageSubText}
                  id="coverages_subtext"
                  data-test-id="coverages_subtext"
                  aria-label={`Coverage subtext: ${row && row.coverageSubText ? `(${row.coverageSubText})` : ""}`}
                >
                  {row && row.coverageSubText ? `(${row.coverageSubText})` : ""}
                </Typography>
              </Box>
            </>
          );
        },
      },
      {
        label: t("Limit"),
        styles: { textAlign: "right" },
        Cell: (row: any) => {
          let defaultVal = "-";
          if (row && row.limit) {
            const limitArray = row.limit.split("/");
            if (limitArray[2]) {
              defaultVal = `${currencyFormat(
                "us",
                limitArray[0]
              )} / ${currencyFormat(
                "us",
                limitArray[2].match(/\d/g).join("")
              )}`;
            } else if (limitArray[1]) {
              defaultVal = `${currencyFormat(
                "us",
                limitArray[0]
              )} / ${currencyFormat("us", limitArray[1])}`;
            } else {
              defaultVal = /\d/.test(limitArray[0])
                ? currencyFormat("us", limitArray[0])
                : row.limit;
            }
          }
          return (
            <Typography
              sx={{ ...CoveragesStyles.coverageText, textAlign: "right" }}
              id="coverages_limit"
              data-test-id="coverages_limit"
              aria-label={`Coverage limit: ${defaultVal}`}
            >
              {defaultVal}
            </Typography>
          );
        },
      },
      {
        label: t("Deductible"),
        styles: { textAlign: "right" },
        Cell: (row: any) => {
          let defaultVal = "-";
          if (row && row.deductible) {
            const deductibleArray = row.deductible.split("/");
            if (deductibleArray[2]) {
              defaultVal = `${currencyFormat(
                "us",
                deductibleArray[0]
              )} / ${currencyFormat(
                "us",
                deductibleArray[2].match(/\d/g).join("")
              )}`;
            } else if (deductibleArray[1]) {
              defaultVal = `${currencyFormat(
                "us",
                deductibleArray[0]
              )} / ${currencyFormat("us", deductibleArray[1])}`;
            } else {
              defaultVal = /\d/.test(deductibleArray[0])
                ? currencyFormat("us", deductibleArray[0])
                : row.limit;
            }
          }
          return (
            <Typography
              sx={{ ...CoveragesStyles.coverageText, textAlign: "right" }}
              id="coverages_deductible"
              data-test-id="coverages_deductible"
              aria-label={`Coverage deductible: ${defaultVal}`}
            >
              {defaultVal}
            </Typography>
          );
        },
      },
    ];

    // Check for lobCode and process coverages accordingly
    if (
      coveragesAndDiscounts?.coverages &&
      coveragesAndDiscounts?.coverages.length > 0
    ) {
      const lobCode = coveragesAndDiscounts.lobCode;

      if (lobCode !== "PA") {
        headerArray.push({
          label: t("Premium"),
          styles: { textAlign: "right" },
          Cell: (row: any) => (
            <Typography
              sx={{ ...CoveragesStyles.coverageText, textAlign: "right" }}
              id="coverages_premium"
              data-test-id="coverages_premium"
              aria-label={`Coverage premium: ${row && row.coveragePremiumAmt && !isNaN(row.coveragePremiumAmt)
                ? currencyFormat("us", row.coveragePremiumAmt)
                : row.coveragePremiumAmt
                ? row.coveragePremiumAmt
                : "-"}`}
            >
              {row && row.coveragePremiumAmt && !isNaN(row.coveragePremiumAmt)
                ? currencyFormat("us", row.coveragePremiumAmt)
                : row.coveragePremiumAmt
                ? row.coveragePremiumAmt
                : "-"}
            </Typography>
          ),
        });
        if (lobCode === "HO") {
          coveragesAndDiscounts.coverages[0].policyCoverages.forEach(
            (coverage) => {
              if (includesCoverages.includes(coverage.productElementCode)) {
                let updatedCoverage = { ...coverage };
                let parts = coverage.coverageName.split("-");
                if (parts.length === 2) {
                  updatedCoverage.coverageName = parts[1].trim();
                }
                dataArray.push(updatedCoverage);
              } else {
                additionalCoverageArray.push(coverage);
              }
            }
          );
        } else {
          coveragesAndDiscounts.coverages[0].policyCoverages.forEach(
            (coverage) => {
              dataArray.push(coverage);
            }
          );
        }
      } else if (lobCode === "PA") {
        coveragesAndDiscounts.coverages.forEach((item) => {
          const vehicleKey = `${item.manufactureYr}-${item.make}-${item.model}`;
          headerArray.push({
            label: vehicleKey,
            styles: { textAlign: "right" },
            Cell: (row: any) => (
              <Typography
                sx={{ ...CoveragesStyles.coverageText, textAlign: "right" }}
                id={`coverages_vehicle_premium_${vehicleKey}`}
                data-test-id={`coverages_vehicle_premium_${vehicleKey}`}
                aria-label={`Premium for vehicle ${vehicleKey}: ${row && row[vehicleKey]
                  ? currencyFormat("us", row[vehicleKey])
                  : "-"}`}
              >
                {row && row[vehicleKey]
                  ? currencyFormat("us", row[vehicleKey])
                  : "-"}
              </Typography>
            ),
          });

          item.policyCoverages.forEach((coverage) => {
            let existingCoverageIndex = dataArray.findIndex(
              (row) => row.coverageName === coverage.coverageName
            );

            if (existingCoverageIndex > -1) {
              dataArray[existingCoverageIndex] = {
                ...dataArray[existingCoverageIndex],
                [vehicleKey]: coverage.coveragePremiumAmt,
              };
            } else {
              const newCoverage = {
                coverageName: coverage.coverageName,
                coverageSubText: coverage.coverageSubText,
                deductible: coverage.deductible,
                limit: coverage.limit,
                description: coverage.description,
                [vehicleKey]: coverage.coveragePremiumAmt,
              };
              dataArray.push(newCoverage);
            }
          });
        });
      }
    }

    return { dataArray, additionalCoverageArray, headerArray };
  };

  const { headerArray, dataArray, additionalCoverageArray } = getCoverageData();

  return (
    <>
      <Popover
        id="coverages_popover"
        data-test-id="coverages_popover"
        aria-label="Coverage information popover"
        sx={{
          pointerEvents: "none",
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 1 }}>{popoverContent}</Typography>
      </Popover>
      <Box 
        sx={CoveragesStyles.container}
        id="coverages_container"
        data-test-id="coverages_container"
        aria-label="Coverages section container"
      >
        {isLoading &&
        calledReducerType === "policyDetails/GetPolicyCoveragesAndDiscounts" ? (
          <CustomSkeleton height={150} />
        ) : (
          <Premium
            perVehicledata={coveragesAndDiscounts?.premiumPerVehicle || []}
            totalPremium={coveragesAndDiscounts?.totalPremiumAmount || null}
          />
        )}
        <CoveragesAndLimits
          columns={headerArray || []}
          data={dataArray || []}
          sectionTitle="Coverages and Limits"
        />
        {coveragesAndDiscounts?.lobCode === "HO" &&
          additionalCoverageArray.length > 0 && (
            <CoveragesAndLimits
              columns={headerArray || []}
              data={
                additionalCoverageArray.sort((a, b) => {
                  if (a.coverageName < b.coverageName) return -1;
                  if (a.coverageName > b.coverageName) return 1;
                  return 0;
                }) || []
              }
              sectionTitle="Additional Coverages and Limits"
            />
          )}
        {coveragesAndDiscounts?.lobCode === "HO" ? (
          <Box 
            sx={{ display: "grid", gap: "30px" }}
            id="coverages_deductibles_section"
            data-test-id="coverages_deductibles_section"
            aria-label="Other deductibles section"
          >
            <Box 
              sx={{ display: "flex", gap: "10px", alignItems: "center" }}
              id="coverages_deductibles_header"
              data-test-id="coverages_deductibles_header"
              aria-label="Other deductibles header"
            >
              <CustomIcon iconName="Coverages" />
              <Typography
                sx={CoveragesAndLimitsStyles.coverageAndLimitsHeadingText}
              >
                {t("Other Deductibles")}
              </Typography>
            </Box>
            <AdditionalDeductibles />
          </Box>
        ) : (
          <></>
        )}
        <Discounts />
      </Box>
      <Box 
        sx={CoveragesStyles.container2}
        id="coverages_responsive_container"
        data-test-id="coverages_responsive_container"
        aria-label="Responsive coverages section"
      >
        {isLoading &&
        calledReducerType === "policyDetails/GetPolicyCoveragesAndDiscounts" ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingY: "0.5rem",
              gap: "0.8rem ",
            }}
            id="coverages_loading_skeleton"
            data-test-id="coverages_loading_skeleton"
            aria-label="Loading coverages data"
          >
            {Array.from(Array(3)).map((_, index) => (
              <CustomSkeleton height={200} key={index} />
            ))}
          </Box>
        ) : (
          <ResponsiveCoverages
            perVehicledata={coveragesAndDiscounts?.premiumPerVehicle || []}
            totalPremium={coveragesAndDiscounts?.totalPremiumAmount || null}
            data={dataArray || []}
            additionalData={
              additionalCoverageArray.sort((a, b) => {
                if (a.coverageName < b.coverageName) return -1;
                if (a.coverageName > b.coverageName) return 1;
                return 0;
              }) || []
            }
            lobCode={coveragesAndDiscounts?.lobCode || null}
          />
        )}
      </Box>
    </>
  );
};

export default Coverages;
